import { mapContentRefs as mapDocumentContentRefs } from '@gitbook/doc-core';
import type { Document } from '@gitbook/slate';

import {
    type ContentReference,
    type RevisionFiles,
    type RevisionPages,
    RevisionPageType,
    type RevisionReusableContents,
    type RevisionVariables,
} from '../types';
import { createPredictableIdGenerator, type IdGenerator } from '../utils';
import type { RunComputeRevisionResult } from './ContentComputer';

/**
 * Transform all relative content references in a document to use the proper predictable IDs
 * from the computed content.
 */
export function transformDocumentComputedIds({
    computedSeed,
    document,
}: {
    computedSeed: string;
    document: Document;
}): Document {
    const idGenerator = createPredictableIdGenerator({
        seed: computedSeed,
    });

    // Update all references to pages and files to use the predictable IDs
    // generated during the computeRevision call.
    return mapDocumentContentRefs(document, (_node, ref) => {
        return mapContentRef({ ref, idGenerator });
    });
}

/**
 * Transform the IDs of a revision to use predictable IDs that are scoped to a computation.
 */
export function transformRevisionComputedIds({
    revision,
    computedSeed,
}: {
    revision: RunComputeRevisionResult;
    computedSeed: string;
}): RunComputeRevisionResult {
    const pages: RevisionPages = {};
    const files: RevisionFiles = {};
    const reusableContents: RevisionReusableContents = {};
    const variables: RevisionVariables = {};
    const result = {
        contentId: revision.contentId,
        pages,
        rootPages: [] as string[],
        files,
        reusableContents,
        variables,
    } satisfies RunComputeRevisionResult;

    const idGenerator = createPredictableIdGenerator({ seed: computedSeed });

    // Use hashed and predictable IDs
    if (revision.files) {
        Object.entries(revision.files).forEach(([inputFileId, inputFile]) => {
            const fileId = idGenerator({ id: inputFileId });
            result.files[fileId] = {
                ...inputFile,
                id: fileId,
            };
        });
    }

    if (revision.pages) {
        Object.entries(revision.pages).forEach(([inputPageId, inputPage]) => {
            const pageId = idGenerator({ id: inputPageId });
            const page = {
                ...inputPage,
                id: pageId,
            };
            if ('pages' in page) {
                page.pages = page.pages.map((childPageId) => idGenerator({ id: childPageId }));
            }

            if (page.type === RevisionPageType.Link) {
                page.target = mapContentRef({ ref: page.target, idGenerator });
            }

            if (page.type === RevisionPageType.Document) {
                if (page.cover) {
                    if (page.cover.ref) {
                        page.cover.ref = mapContentRef({ ref: page.cover.ref, idGenerator });
                    }
                    if (page.cover.refDark) {
                        page.cover.refDark = mapContentRef({
                            ref: page.cover.refDark,
                            idGenerator,
                        });
                    }
                }

                if (page.metaLinks) {
                    if (page.metaLinks.canonical) {
                        page.metaLinks.canonical = mapContentRef({
                            ref: page.metaLinks.canonical,
                            idGenerator,
                        });
                    }
                    if (page.metaLinks.alternates) {
                        page.metaLinks.alternates = page.metaLinks.alternates.map((alternate) =>
                            mapContentRef({ ref: alternate, idGenerator })
                        );
                    }
                }
            }

            if (page.type === RevisionPageType.Computed) {
                if (page.computed.dependencies) {
                    Object.entries(page.computed.dependencies).forEach(
                        ([dependencyId, dependency]) => {
                            if (dependency.ref && page.computed.dependencies?.[dependencyId]) {
                                page.computed.dependencies[dependencyId].ref = mapContentRef({
                                    ref: dependency.ref,
                                    idGenerator,
                                });
                            }
                        }
                    );
                }
            }

            result.pages[pageId] = page;
        });
        result.rootPages = revision.rootPages.map((pageId) => idGenerator({ id: pageId }));
    }

    // Copy variables as-is
    result.variables = { ...(revision.variables ?? {}) };

    // Remap reusable contents IDs
    if (revision.reusableContents) {
        Object.entries(revision.reusableContents).forEach(([inputId, ruc]) => {
            const reusableId = idGenerator({ id: inputId });
            result.reusableContents ??= {};
            result.reusableContents[reusableId] = {
                ...ruc,
                id: reusableId,
            };
        });
    }

    return result;
}

/**
 * Transform a local content ref (relative to the current space) to use the predictable IDs
 * generated during the content computation.
 */
function mapContentRef({
    ref,
    idGenerator,
}: {
    ref: ContentReference;
    idGenerator: IdGenerator;
}): ContentReference {
    switch (ref.kind) {
        case 'page': {
            if (!ref.space) {
                return {
                    ...ref,
                    page: idGenerator({
                        id: ref.page,
                    }),
                };
            }
            return ref;
        }
        case 'file': {
            if (!ref.space) {
                return {
                    ...ref,
                    file: idGenerator({
                        id: ref.file,
                    }),
                };
            }
            return ref;
        }
        case 'anchor': {
            if (!ref.space && ref.page) {
                return {
                    ...ref,
                    page: idGenerator({
                        id: ref.page,
                    }),
                };
            }

            return ref;
        }
        case 'reusable-content': {
            if (!ref.space) {
                return {
                    ...ref,
                    reusableContent: idGenerator({ id: ref.reusableContent }),
                };
            }

            return ref;
        }
        default:
            return ref;
    }
}
